import { useState } from 'react';
import {
  EXTRACT_AS_CATEGORIES,
  type ExtractAsCategory,
} from '@avisoma/shared/dist/api-schema/paths/extract-as-categories.js';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../../catalyst/dropdown';
import { ConfirmDialog } from '../../components/ConfirmDialog';

/** Exhaustive: a new EXTRACT_AS_CATEGORIES entry without a label fails `pnpm build`. */
const EXTRACT_AS_LABELS: Record<ExtractAsCategory, string> = {
  'Engine Installation/Removal History': 'Engine',
  'Engine Operational Report': 'Engine Operational Report (engine history)',
  'Part Movement Traceability Card': 'Part Movement',
  'LLP Status': 'LLP Status',
  'AD Status': 'AD',
  'SB Status': 'SB',
  'SB Statement': 'SB Statement',
  'LDND': 'LDND',
  'Bill of Sale': 'Bill of Sale',
  'Recorded Modification Sheet': 'RMS (AD + SB)',
  'AD-SB-LLP Status Sheet': 'AD-SB-LLP Sheet (AD + SB)',
  'LLP - EDS': 'LLP EDS (AD + SB)',
  'LLP - VSL': 'LLP VSL (AD + SB)',
  'P/N Detail Print': 'P/N Detail Print (LLP + AD + SB + QEC + Blades)',
  'Engine Parts Release Certificate': 'ARC — Engine Parts',
  'Fan Blade Release Certificates': 'ARC — Fan Blade',
  'Engine Release Certificate': 'ARC — Engine',
  'Accessory and QEC Release Certificates': 'ARC — Accessory/QEC',
  'LLP Release Certificate': 'ARC — LLP',
  'Module Release Certificate': 'ARC — Module',
  'Accessory and QEC List': 'QEC List',
  'Engine Configuration List': 'Engine Config List (LLP + QEC)',
  'Utilisation Records': 'Utilisation',
  'Engine Manufacture Certificate': 'Engine Manufacture Cert',
  'Manufacture Logbook': 'Manufacture Logbook (birth date)',
  'PMA/DER Status': 'PMA/DER',
  'In House Modifications Statement': 'In House Modifications Statement (PMA/DER)',
  'No Exceedance Statement': 'No Exceedance Statement',
  'Engine Workscope': 'Engine Workscope',
  'Borescope Report': 'Borescope Report',
  'Repair Report': 'Repair Report',
  'Accessory/Parts Teardown Report': 'Teardown Report (repair)',
  'NIS Statement': 'NIS',
  'Thrust Rating Change Records': 'Thrust Change',
  'Engine Install Removal Job Cards': 'Engine Install/Removal Job Card',
  'Current TSN and CSN Statement': 'Current TSN/CSN',
  'Aircraft Readiness Log / Aircraft Inspection Report': 'Aircraft Delivery Record (build records)',
  'LLP - DIS': 'LLP DIS (build record)',
  'Tech Log Sheets': 'Tech Log Sheet (flight totals)',
  'Ferry Flight Statement': 'Ferry Flight Statement',
  'Removal Tags': 'Removal Tag (part tag)',
  'Airworthiness Directives Reference': 'AD Text (SB references)',
  'OEM Concessions Statement': 'OEM Concessions Statement',
  'Major Repair or Alteration Form': 'Major Repair/Alteration Form',
  'DICA Records': 'DICA Records',
  'HPT Blade Status': 'HPT Blade Status (blade sheet)',
  'HPT Nozzles': 'HPT Nozzles (nozzle sheet)',
  'LPT Nozzles': 'LPT Nozzles (nozzle sheet)',
  'Fan Blades Distribution List': 'Fan Blades Distribution List (fan blade sheet)',
  'Fan Blade Installation Records': 'Fan Blade Installation Records (fan blade sheet)',
};

const EXTRACT_AS_OPTIONS = EXTRACT_AS_CATEGORIES.map((category) => ({
  category,
  label: EXTRACT_AS_LABELS[category],
}));

interface ExtractAsMenuProps {
  currentCategory: string | null;
  hasResult: boolean;
  onPick: (category: string) => Promise<void>;
}

export function ExtractAsMenu({ currentCategory, hasResult, onPick }: ExtractAsMenuProps) {
  const [pendingCategory, setPendingCategory] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(false);

  const runExtraction = async (category: string) => {
    try {
      setIsExtracting(true);
      await onPick(category);
    } catch (err) {
      console.error('extract-as failed', err);
    } finally {
      setIsExtracting(false);
      setPendingCategory(null);
    }
  };

  const handlePick = async (category: string) => {
    if (hasResult) {
      setPendingCategory(category);
      return;
    }
    await runExtraction(category);
  };

  return (
    <>
      <Dropdown>
        <DropdownButton
          as="button"
          className="text-xs px-2 py-0.5 rounded border border-zinc-300 dark:border-zinc-600 hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          Force extract as…
        </DropdownButton>
        <DropdownMenu anchor="bottom end" className="min-w-[180px]">
          {EXTRACT_AS_OPTIONS.map((opt) => (
            <DropdownItem
              key={opt.category}
              current={currentCategory === opt.category}
              onClick={() => void handlePick(opt.category)}
            >
              <DropdownLabel>{opt.label}</DropdownLabel>
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>

      <ConfirmDialog
        open={pendingCategory !== null}
        title="Replace Extraction Result"
        message={`Replace the existing extraction result by re-running extraction as "${pendingCategory ?? ''}"?`}
        confirmLabel="Replace"
        pendingLabel="Replacing…"
        isPending={isExtracting}
        onConfirm={() => void (pendingCategory && runExtraction(pendingCategory))}
        onClose={() => setPendingCategory(null)}
      />
    </>
  );
}
